import React, { useEffect, useState } from "react";
import { Theme, PaletteMode, ThemeOptions, useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import Axios, { InternalAxiosRequestConfig } from "axios";
import type Keycloak from "keycloak-js";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { RouteProps } from "react-router-dom";
import { Redirect, Route, RouteComponentProps } from "react-router-dom";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { handleTokenUpdate } from "../../app/sockets/socket";
import {
  setRequestInterceptor,
  setSettings,
  setUserInformation,
  setUserRoles
} from "../../features/Login/slices/login.slice";
import { UserRole } from "../../features/Login/typings/auth.types";
import { MetaData, PricingModel, UserRolesFlags } from "../../features/Login/typings/login.types";
import { AppSettings } from "../../features/Settings/typings/user-settings.types";
import { useRemoteFetchUserProfile } from "../../hooks/Remote/User/useRemoteFetchUserProfile";
import { useRemoteFetchUserSettings } from "../../hooks/Remote/User/useRemoteFetchUserSettings";
import { WelcomeFade } from "../Animations/welcomeFadeAnimation.component";
import { OpenAPI } from "../../features/Api-legacy/type/openApi";
import { claimImportedRoomBookings } from "../../utils/axios-requests";
import { runAfterLoginBootstrap } from "../../utils/run-after-login-bootstrap";
import { toValidMuiColor } from "../../utils/theme-color.utils";
import { makeSeeditThemeOptions } from "../../themes/make-seedit-theme";

interface PrivateRouteParams extends RouteProps {
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  setTheme: React.Dispatch<React.SetStateAction<ThemeOptions>>;
  setBrandColor: React.Dispatch<React.SetStateAction<string>>;
}

export function PrivateRoute({
  setTheme,
  setBrandColor,
  component: Component,
  ...rest
}: Readonly<PrivateRouteParams>) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const {
    settings: { colorMode }
  } = useSelector((state: RootState) => state.login);
  const dispatch = useDispatch();

  const [metaDataState, setMetaDataState] = useState<MetaData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const { keycloak } = useKeycloak();

  const { data: userProfile, error: userProfileError } = useRemoteFetchUserProfile();
  const { data: userSettings, error: userSettingsError } = useRemoteFetchUserSettings();

  useEffect(() => {
    if (userProfileError || userSettingsError) keycloak.logout();
  }, [userProfileError, userSettingsError]);

  /** store user profile information */
  useEffect(() => {
    if (!userProfile) return;
    if (!keycloak.authenticated || !keycloak.token) return;

    setMetaDataState(userProfile.company.meta);
    setTheme(makeTheme(theme, colorMode, toValidMuiColor(userProfile.company.meta?.color)));
    // in case user profile doesn't have pricingModels
    if (!userProfile.pricingModels) userProfile.pricingModels = [PricingModel.FREEMIUM];
    dispatch(setUserInformation(userProfile));

    setTimeout(() => setIsLoading(false), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per profile load
  }, [userProfile]);

  /** Best-effort room import claim — deferred so it does not compete with dashboard schedule loads. */
  useEffect(() => {
    if (!userProfile) return;
    if (!keycloak.authenticated || !keycloak.token) return;

    const cancelDeferredClaim = runAfterLoginBootstrap(() => {
      claimImportedRoomBookings()
        .then(response => {
          const count = response.data?.claimedCount ?? 0;
          if (count > 0) {
            enqueueSnackbar(t("_roomImportClaim_success", { count }), {
              variant: "success",
              autoHideDuration: 8000
            });
          }
        })
        .catch(() => {
          /* Import claim is best-effort; do not block login */
        });
    });

    return cancelDeferredClaim;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per profile session; notistack/i18n refs stable
  }, [userProfile, keycloak.authenticated, keycloak.token]);

  /** set language and store settings when userSettings are fetched */
  useEffect(() => {
    if (!userSettings) return;
    // assign english language if no other language is set
    if (userSettings.language === "") userSettings.language = "en";
    i18n.changeLanguage(userSettings.language).then();
    dispatch(setSettings({ settings: makeData(userSettings) }));
  }, [userSettings]);

  /** refresh token checking interval */
  useEffect(() => {
    // Refresh token every 60 minutes (3600000 ms)
    const interval = setInterval(() => {
      keycloak.updateToken(60).catch(() => {
        /* istanbul ignore next */
        keycloak.login(); // Redirect if token cannot be refreshed
      });
    }, 3600000); // 60 minutes

    // Fallback: trigger re-auth if token is already expired
    keycloak.onTokenExpired = () => /* istanbul ignore next */ {
      keycloak
        .updateToken(0)
        .then((refreshed: boolean) => {
          if (!refreshed) {
            keycloak.login(); // Token expired and not refreshed
          }
        })
        .catch(() => {
          keycloak.login(); // Error while trying to refresh
        });
    };

    return () => {
      clearInterval(interval);
      keycloak.onTokenExpired = undefined; // Clean up the handler
    };
  }, []);

  /** listen for the visibilitychange or focus event to re-check the token when the user returns */
  /* istanbul ignore next */
  useEffect(() => {
    const checkToken = () => {
      keycloak.updateToken(60).catch(() => {
        keycloak.login(); // Redirect if refresh fails
      });
    };

    window.addEventListener("focus", checkToken);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        checkToken();
      }
    });

    return () => {
      window.removeEventListener("focus", checkToken);
      document.removeEventListener("visibilitychange", checkToken);
    };
  }, []);

  /** handle login with keycloak */
  useEffect(() => {
    setToken(keycloak, dispatch);
    keycloak.onAuthRefreshSuccess = () => setToken(keycloak, dispatch);
    keycloak.onAuthRefreshError = keycloak.logout;
  }, [keycloak]);

  /** update theme on demand */
  useEffect(() => {
    const validColor = toValidMuiColor(metaDataState?.color);
    setBrandColor(validColor);
    setTheme(makeTheme(theme, colorMode, validColor));
  }, [colorMode, metaDataState]);

  // Update socket connection
  useEffect(() => {
    if (keycloak.token) {
      handleTokenUpdate(keycloak.token);
    }
  }, [keycloak.token]);

  return (
    <Route
      {...rest}
      render={props => {
        if (keycloak?.authenticated && isLoading) {
          return <WelcomeFade text={t("Welcome to WePlace")} />;
        } else if (keycloak?.authenticated) {
          return <Component {...props} />;
        } else {
          return (
            <Redirect
              to={{
                pathname: "/login",
                state: { from: props.location }
              }}
            />
          );
        }
      }}
    />
  );
}

function hasKeycloakRole(keycloak: Keycloak, role: UserRole): boolean {
  return keycloak.hasRealmRole(role) || (keycloak.hasResourceRole?.(role) ?? false);
}

function getUserRoles(keycloak: Keycloak): UserRolesFlags {
  return {
    isTenantAdmin: hasKeycloakRole(keycloak, UserRole.isTenantAdmin),
    isCompanyAdmin: hasKeycloakRole(keycloak, UserRole.isCompanyAdmin),
    isLocationAdmin: hasKeycloakRole(keycloak, UserRole.isLocationAdmin),
    isHrAdmin: hasKeycloakRole(keycloak, UserRole.isHrAdmin),
    isFinanceAdmin: hasKeycloakRole(keycloak, UserRole.isFinanceAdmin),
    isCapacityResponsible: hasKeycloakRole(keycloak, UserRole.isCapacityResponsible),
    isDeviceResponsible: hasKeycloakRole(keycloak, UserRole.isEquipmentResponsible),
    isCostCenterResponsible: hasKeycloakRole(keycloak, UserRole.isCostCenterResponsible),
    isEmployeeResponsible: hasKeycloakRole(keycloak, UserRole.isEmployeeResponsible),
    isProjectResponsible: hasKeycloakRole(keycloak, UserRole.isProjectResponsible),
    isWorkOrderResponsible: hasKeycloakRole(keycloak, UserRole.isWorkOrderResponsible),
    isZoneBookingCapable: hasKeycloakRole(keycloak, UserRole.isZoneBookingCapable),
    isRequestAdmin: hasKeycloakRole(keycloak, UserRole.isRequestAdmin),
    isFleetAdmin: hasKeycloakRole(keycloak, UserRole.isFleetAdmin),
    isVisitorAdmin: hasKeycloakRole(keycloak, UserRole.isVisitorAdmin)
  };
}

/**
 * function to attach authorization header
 * used to attach : cfg.headers = { ...cfg.headers, Authorization: `Bearer ${keycloak.token}` };
 */
export const getAttachAuthorizationHeaderFunction = (keycloak: Keycloak) => {
  return async (cfg: InternalAxiosRequestConfig) => {
    /** cfg.baseURL = bookingBackend */
    cfg.headers.Authorization = `Bearer ${keycloak.token}`;

    return cfg;
  };
};

const setToken = (keycloak: Keycloak, dispatch: ReturnType<typeof useDispatch>) => {
  if (keycloak.authenticated && keycloak.token) {
    OpenAPI.TOKEN = keycloak.token;

    const interceptorId = Axios.interceptors.request.use(
      getAttachAuthorizationHeaderFunction(keycloak)
    );

    dispatch(setRequestInterceptor(interceptorId));
    dispatch(setUserRoles(getUserRoles(keycloak)));
  }
};

export function makeTheme(
  _theme: Theme,
  colorMode: PaletteMode,
  brandColor = "#006651"
): ThemeOptions {
  return makeSeeditThemeOptions(colorMode, brandColor);
}

export function makeData(data: any): AppSettings {
  return {
    useDeviceTimezone: data.useDeviceTimezone,
    colorMode: data.colorMode,
    timezone: data.timezone,
    preferredPlaceCategories: data.preferredPlaceCategories,
    preferredEquipmentCategories: data.preferredEquipmentCategories,
    preferredLocations: data.preferredLocations,
    standardStartTime: data.defaultStartTime,
    standardEndTime: data.defaultEndTime,
    // Automation is not implemented in backend
    isZoneAutomation: false,
    useAutomatedBooking: false,
    useAutomatedZoneBooking: false,
    advancedBookingLength: 0,
    numberOfActiveZones: 0,
    language: data.language,
    weekdays: [
      { location: "OV", active: false },
      { location: "OV", active: false },
      { location: "OV", active: false },
      { location: "OV", active: false },
      { location: "OV", active: false },
      { location: "OV", active: false }
    ],
    lastLogin: null,
    notificationSettings: data.notificationSettings,
    enabledBookingNotifications: data.enabledBookingNotifications,
    enabledSyncs: data.enabledSyncs
  };
}
